import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import DonorDetail from './DonorDetail';
import useStore from '../useStore';
import * as XLSX from 'xlsx';

function LoadingIndicator() {
  return (
    <div className="loading-container">
      <div className="spinner"></div>
      <div className="loading-label">Loading</div>
    </div>
  );
}

const GENERIC_VOUCHER_ID = '11111111-1111-1111-1111-111111111111';
const GENERIC_VOUCHER_MESSAGE = `As part of our year-end financial review, we identified one or more outstanding check payments issued to your organization that have not cleared. The payment provided represents the aggregate total of all uncleared checks. Because our review covered a period extending back as far as 10 years, we are unable to provide the donor-level details associated with the original payments. Accordingly, the distribution to your organization has been made based solely on the total uncleared check amount.

We’re sorry for any inconvenience this may have caused.

The Agency Hub Team
United Way of Greater Los Angeles`;

function AllocationList({ check, onBack }) {
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [genericMessage, setGenericMessage] = useState(null);
  const [selectedAllocation, setSelectedAllocation] = useState(null);

  const { selectedAgency } = useStore();

  const isGenericVoucher = check.payoutVoucherId?.toLowerCase() === GENERIC_VOUCHER_ID;

  useEffect(() => {
    // Bulk-imported checks have no real Dataverse voucher, so there's no allocation
    // detail to fetch — skip the call entirely and show the generic message.
    if (isGenericVoucher) {
      setLoading(false);
      return;
    }

    axios.get(`${process.env.REACT_APP_API_URL}/Allocations?payVouId=${check.payoutVoucherId}&desig=${selectedAgency.vendorId}`)
      .then((response) => {
        if (typeof response.data === 'string') {
          setGenericMessage(response.data);
          setLoading(false);
          return;
        }
        const dataWithIds = response.data.map((allocation, index) => ({
          ...allocation,
          id: allocation.id || index,
          payments: allocation.payments || []
        }));
        setAllocations(dataWithIds);
        setLoading(false);
      })
      .catch((error) => {
        if (error.response) {
          console.log('Response error:', error.response.data);
          setError(error.response.data);
        } else if (error.request) {
          console.log('Request error:', error.request);
          setError('No response received from server');
        } else {
          console.log('General error:', error.message);
          setError(error.message);
        }
        setLoading(false);
      });
  }, [check, selectedAgency, isGenericVoucher]);

  if (loading) return <LoadingIndicator />;

  if (isGenericVoucher || genericMessage) {
    return (
      <div>
        <button className="gradient-button" onClick={onBack}>Back to Checks</button>
        <h2>Allocations for Check {check.checkNumber}</h2>
        <div style={{ whiteSpace: 'pre-line' }}>{genericMessage || GENERIC_VOUCHER_MESSAGE}</div>
      </div>
    );
  }

  if (error) return <div>Error: {typeof error === 'string' ? error : error.message}</div>;

  const columns = [
    { field: 'donorId', headerName: 'Donor Id', width: 150 },
    { field: 'donor', headerName: 'Donor', width: 150 },
    { field: 'employerID', headerName: 'Employer ID', width: 150 },
    { field: 'employer', headerName: 'Employer', width: 150 },
    { field: 'programName', headerName: 'Program Name', width: 150 },
    {
      field: 'pledgeAmount', headerName: 'Pledge Amount', width: 150, valueFormatter: (params) => {
        const v = parseFloat(params)
        return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      }
    },
    {
      field: 'grossAmount', headerName: 'Gross Amount', width: 150, valueFormatter: (params) => {
        const v = parseFloat(params)
        return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      }
    },
    {
      field: 'desigFees', headerName: 'Desig Fees', width: 150, valueFormatter: (params) => {
        const v = parseFloat(params)
        return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      }
    },
    {
      field: 'netPayment', headerName: 'Net Payment', width: 150, valueFormatter: (params) => {
        const v = parseFloat(params)
        return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      }
    },
    { field: 'year', headerName: 'Year', width: 150 },
    { field: 'allocationId', headerName: 'AllocationId', width: 150 }
  ];

  const handleRowClick = (params) => {
    setSelectedAllocation(params.row);
  };

  const columnVisibilityModel = {
    allocationId: false
  };

  const downloadExcel = () => {
    const formattedData = allocations.map(allocation => {
      const formattedRow = {};
      columns.forEach(column => {
        formattedRow[column.headerName] = allocation[column.field];
      });
      return formattedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Allocations");

    XLSX.writeFile(workbook, `Allocations_${check.checkNumber}.xlsx`);
  };

  const downloadCSV = () => {
    const csvData = allocations.map(allocation => {
      return columns.map(column => allocation[column.field]).join(",");
    });

    const csvHeader = columns.map(column => column.headerName).join(",");
    const csvContent = [csvHeader, ...csvData].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Allocations_${check.checkNumber}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {selectedAllocation ? (
        <DonorDetail allocation={selectedAllocation} onBack={() => setSelectedAllocation(null)} />
      ) : (
        <div style={{ height: 400, width: '100%' }}>
          <button className="gradient-button margin-right" onClick={onBack}>Back to Checks</button>
          <button className="gradient-button margin-right" onClick={downloadExcel}>Download Excel</button>
          <button className="gradient-button" onClick={downloadCSV}>Download CSV</button>
          <h2>Allocations for Check {check.checkNumber}</h2>
          <DataGrid
            rows={allocations}
            columns={columns}
            pageSize={5}
            onRowClick={handleRowClick}
            columnVisibilityModel={columnVisibilityModel}
          />
        </div>
      )}
    </div>
  );
}

export default AllocationList;